import React, {useEffect, useState} from 'react';
import { Link } from "react-router-dom";
import LocalizedText from "../../presentational/LocalizedText";
import Checkbox from "../../presentational/Checkbox";
import Button from "../../presentational/Button";
import Input from "../../presentational/Input";
import useTranslate from "../../../hooks/useTranslate";
import './LoginPassword.css'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-regular-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useTurnstile } from "../../../hooks/useTurnstile";

interface LoginPasswordProps {
  isLoading: boolean;
  emailOrPhone: string;
  rememberMe: boolean;
  serverError: string;
  notYouLink: string;
  isValidPassword: (password: string) => boolean;
  finishLogin: (password: string) => void;
  forgotPassword: () => void;
  setEmailOrPhone: (emailOrPhone: string) => void;
  setRememberMe: (rememberMe: boolean) => void;
}

const LoginPassword: React.FC<LoginPasswordProps> = ({
  isLoading,
  emailOrPhone,
  rememberMe,
  serverError,
  notYouLink,
  finishLogin,
  forgotPassword,
  isValidPassword,
  setEmailOrPhone,
  setRememberMe,
}) => {
  const [password, setPassword] = useState('')
  const [unmaskPassword, setUnmaskPassword] = useState(false)
  const translations = useTranslate(["participant-login-password-label", "participant-login-password-placeholder", "participant-login-remember-me", "participant-login-next-label"])
  useTurnstile();

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
  }

  const handleChangeEmailOrPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailOrPhone(e.target.value)
  }

  const handleChangeRememberMe = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRememberMe(e.target.checked)
  }

  const handleNotYouClick = () => {
    setEmailOrPhone('')
    setRememberMe(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    finishLogin(password)
    return false
  }

  return (
    <form className="login-action login-password" onSubmit={handleSubmit}>
      <p className="personalized-welcome-back">
        <h3 className="personalized-welcome-back-title"><LocalizedText textKey="participant-login-login-password-title" /></h3>
        <span className="personalized-welcome-back-userid"> {emailOrPhone}</span>
        <Button variant="minimal" intent="primary" to={notYouLink} onClick={handleNotYouClick}><LocalizedText textKey="participant-login-not-you-label" /></Button>
      </p>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <div className="field-group">
        <label htmlFor="password">{translations["participant-login-password-label"]}</label>
        <input type="text" id="username" value={emailOrPhone} onChange={handleChangeEmailOrPhone} onInput={handleChangeEmailOrPhone} autoComplete="username" hidden />
        <div className="combo-input-group">
          <Input
            onChange={handleChangePassword}
            autoComplete="current-password"
            className="autofocus"
            disabled={isLoading}
            id="password"
            type={unmaskPassword ? 'text' : 'password'}
            value={password}
            placeholder={translations["participant-login-password-placeholder"]}
            fullWidth={true}
            htmlAttributes={{
              onInput: handleChangePassword,
              maxLength: 64,
              autoFocus: true,
            }}
          />
          <button
            type="button"
            onClick={() => setUnmaskPassword(!unmaskPassword)}
            className="password-mask-toggle button"
            title="Toggle password visibility">
               <FontAwesomeIcon icon={unmaskPassword ? faEye as IconProp: faEyeSlash as IconProp} />
          </button>
        </div>
      </div>
      <Checkbox
        className="login-field"
        identifier="rememberMe"
        checked={rememberMe}
        onChange={handleChangeRememberMe}
        label={translations["participant-login-remember-me"]}
      />
      <div>
        <div id="cf-turnstile" className="cf-turnstile"></div>
        <Button
          onClick={handleSubmit}
          disabled={!isValidPassword(password) || isLoading}
          className="primary-action"
          intent='primary'
          width="100%"
          loading={isLoading}
          type='submit'
        >
          <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
        </Button>
      </div>
      <Button
        className="forgot-password-button"
        onClick={forgotPassword}
        width='100%'
        intent='secondary'
        variant='minimal'
      >
        <LocalizedText textKey='participant-login-forgot-password-label' />
      </Button>
    </form>
  )
}

export default LoginPassword